import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Text, 
  VStack, 
  HStack, 
  Flex, 
  Badge, 
  useDisclosure, 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalCloseButton, 
  ModalFooter,
  Icon,
  CircularProgress,
  CircularProgressLabel
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaDharmachakra, FaCoins, FaTrophy } from 'react-icons/fa';

// Time box component for countdown display
const TimeBox = ({ value, label, color = "purple.300" }) => (
  <Box 
    bg="gray.700" 
    borderRadius="md" 
    px={3} 
    py={2} 
    textAlign="center"
    boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
  >
    <Text fontSize="xl" fontWeight="bold" color={color}>
      {value}
    </Text>
    <Text fontSize="xs" color="gray.400">
      {label}
    </Text>
  </Box>
);

/**
 * ReusableFortuneWheel - A configurable fortune wheel component
 * 
 * @param {Object} props - Component properties
 * @param {Object} props.config - Configuration for the wheel
 * @param {string} props.config.title - Title of the wheel game
 * @param {string} props.config.description - Description of the wheel game
 * @param {Array} props.config.segments - Array of segment objects with name, color, value, and probability properties
 * @param {Object} props.config.prizes - Configuration for prizes (min, max, currency)
 * @param {string} props.config.titleGradient - Gradient for the title
 * @param {number} props.config.spinDuration - Duration of the spin animation in ms
 * @param {number} props.config.spinRotations - Number of full rotations during spin
 * @param {Function} props.onWin - Callback function when a win occurs
 * @param {Function} props.onSpin - Optional callback function when wheel is spun
 * @param {boolean} props.showPlayerList - Whether to show the player list
 * @param {string} props.theme - Theme color scheme ("default", "pirate", "space", "fantasy")
 * @param {number} props.size - Size of the wheel (default: 600)
 * @param {boolean} props.autoSpin - Whether the wheel should spin automatically when rendered
 * @param {Object} props.batchInfo - Information about the current batch (current, total, etc.)
 * @returns {JSX.Element} The ReusableFortuneWheel component
 */
const ReusableFortuneWheel = ({ 
  config = {}, 
  onWin = () => {}, 
  onSpin = () => {}, 
  showPlayerList = false,
  theme = "default",
  size = 600,
  autoSpin = false,
  batchInfo = { current: 0, total: 100, batchNumber: 1 }
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [lastWin, setLastWin] = useState(null);
  const [nextDrawDate, setNextDrawDate] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Default configuration
  const defaultConfig = {
    title: "Fortune Wheel",
    description: "Spin the wheel for a chance to win prizes!",
    titleGradient: "linear(to-r, purple.400, blue.400)",
    descriptionColor: "gray.400",
    titleFontSize: "3xl",
    spinDuration: 5000,
    spinRotations: 1440, // 4 full rotations
    prizes: { min: 1000, max: 1000, currency: "JINU" },
    segments: [
      { name: "Grand Prize", color: "#E53E3E", value: 1000, probability: 5 },
      { name: "Big Win", color: "#805AD5", value: 500, probability: 10 },
      { name: "Medium Win", color: "#3182CE", value: 250, probability: 15 },
      { name: "Small Win", color: "#38A169", value: 100, probability: 20 },
      { name: "Mini Win", color: "#DD6B20", value: 50, probability: 25 },
      { name: "Try Again", color: "#718096", value: 0, probability: 25 }
    ]
  };

  // Merge provided config with defaults
  const mergedConfig = { ...defaultConfig, ...config };
  
  // Calculate total probability and normalize if needed
  const totalProbability = mergedConfig.segments.reduce((sum, segment) => sum + (segment.probability || 0), 0);
  if (totalProbability !== 100) {
    // Normalize probabilities to sum to 100
    mergedConfig.segments = mergedConfig.segments.map(segment => ({
      ...segment,
      probability: (segment.probability / totalProbability) * 100
    }));
  }

  // Calculate degrees for each segment based on probability
  let currentAngle = 0;
  mergedConfig.segments = mergedConfig.segments.map(segment => {
    const degrees = (segment.probability / 100) * 360;
    const startAngle = currentAngle;
    currentAngle += degrees;
    return {
      ...segment,
      startAngle,
      degrees,
      endAngle: currentAngle
    };
  });

  // Auto-spin when batch fills or when autoSpin is true
  useEffect(() => {
    // Check if we should auto-spin based on the batch configuration
    // This handles any batch size (100, 10000, etc.)
    const batchTotal = batchInfo?.total || 100; // Default to 100 if not specified
    const batchCurrent = batchInfo?.current || 0;
    
    // Auto-spin when explicitly set or when current count reaches total
    const shouldAutoSpin = autoSpin || (batchCurrent >= batchTotal);
    
    if (shouldAutoSpin && !isSpinning) {
      // Add a slight delay before spinning to ensure component is fully rendered
      const timer = setTimeout(() => {
        console.log(`Auto-spinning wheel: ${batchCurrent}/${batchTotal} games played`);
        spinWheel();
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [autoSpin, batchInfo, isSpinning]);
  
  // Set up countdown timer
  useEffect(() => {
    const calculateNextDrawDate = () => {
      const now = new Date();
      const nextDraw = new Date();
      
      // Set to next Friday at 8:00 PM
      nextDraw.setDate(now.getDate() + (5 - now.getDay() + 7) % 7);
      nextDraw.setHours(20, 0, 0, 0);
      
      // If today is Friday and it's before 8:00 PM, set to today
      if (now.getDay() === 5 && now.getHours() < 20) {
        nextDraw.setDate(now.getDate());
      }
      
      return nextDraw;
    };
    
    const updateTimer = () => {
      if (!nextDrawDate) return;
      
      const now = new Date();
      const difference = nextDrawDate.getTime() - now.getTime();
      
      if (difference <= 0) {
        // Timer expired, set new draw date
        setNextDrawDate(calculateNextDrawDate());
        return;
      }
      
      // Calculate time units
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      setTimeLeft({
        days,
        hours,
        minutes,
        seconds
      });
    };
    
    // Set initial next draw date and start timer
    if (!nextDrawDate) {
      setNextDrawDate(calculateNextDrawDate());
    }
    
    // Update timer every second
    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [nextDrawDate]);

  // Function to spin the wheel
  const spinWheel = () => {
    if (isSpinning) return;
    
    setIsSpinning(true);
    
    // Call the onSpin callback if provided
    onSpin();
    
    // Determine winner based on weighted probabilities
    const randomValue = Math.random() * 100;
    let cumulativePercentage = 0;
    let winnerIndex = 0;
    
    for (let i = 0; i < mergedConfig.segments.length; i++) {
      cumulativePercentage += mergedConfig.segments[i].probability;
      if (randomValue <= cumulativePercentage) {
        winnerIndex = i;
        break;
      }
    }
    
    // Calculate rotation to land on the winner
    const winningSegment = mergedConfig.segments[winnerIndex];
    
    // Middle of the segment
    const middleAngle = winningSegment.startAngle + (winningSegment.degrees / 2);
    
    // To make the wheel land with the pointer at the middle of the segment,
    // we need to rotate to (360 - middleAngle) plus some additional full rotations
    const baseRotation = 360 - middleAngle;
    const additionalRotations = mergedConfig.spinRotations;
    
    // Set the final rotation value
    const newRotation = baseRotation + additionalRotations;
    setRotation(newRotation);
    
    // After spinning animation completes
    setTimeout(() => {
      const winner = mergedConfig.segments[winnerIndex];
      
      // Calculate prize amount
      const prizeAmount = winner.value || 
                        Math.floor(Math.random() * (mergedConfig.prizes.max - mergedConfig.prizes.min + 1)) + mergedConfig.prizes.min;
      
      // Record the win
      const winData = {
        segment: winner,
        amount: prizeAmount,
        currency: mergedConfig.prizes.currency,
        timestamp: new Date()
      };
      
      setLastWin(winData);
      
      // Call the onWin callback with the win data
      onWin(winData);
      
      // Show win modal if there's a prize
      if (prizeAmount > 0) {
        onOpen();
      }
      
      setIsSpinning(false);
    }, mergedConfig.spinDuration);
  };

  // Wheel component
  const FortuneWheelInner = () => {
    const wheelSize = size;
    const center = wheelSize / 2;
    
    return (
      <Box position="relative" width={`${wheelSize}px`} height={`${wheelSize}px`}>
        {/* Outer ring with glow effect */}
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          width={`${wheelSize + 60}px`}
          height={`${wheelSize + 60}px`}
          borderRadius="full"
          bg="transparent"
          boxShadow={`0 0 60px ${theme === "pirate" ? "rgba(255, 215, 0, 0.5)" : "rgba(138, 43, 226, 0.5)"}`}
        />
        
        {/* Rotating Wheel */}
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            position: "absolute",
            top: 0,
            left: 0,
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
            transform: `rotate(${rotation}deg)`,
            transition: isSpinning ? `transform ${mergedConfig.spinDuration / 1000}s cubic-bezier(0.2, 0.8, 0.2, 1)` : "none"
          }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${wheelSize} ${wheelSize}`}
          >
            {/* Draw wheel segments */}
            {mergedConfig.segments.map((segment, index) => {
              // Calculate segment path
              const startAngle = segment.startAngle * (Math.PI / 180);
              const endAngle = segment.endAngle * (Math.PI / 180);
              
              const x1 = center + center * Math.cos(startAngle);
              const y1 = center + center * Math.sin(startAngle);
              const x2 = center + center * Math.cos(endAngle);
              const y2 = center + center * Math.sin(endAngle);
              
              // Determine if segment angle is greater than 180 degrees
              const largeArcFlag = segment.degrees > 180 ? 1 : 0;
              
              // Create path for segment
              const pathData = [
                `M ${center} ${center}`,
                `L ${x1} ${y1}`,
                `A ${center} ${center} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                "Z"
              ].join(" ");
              
              return (
                <g key={index}>
                  {/* Segment */}
                  <path
                    d={pathData}
                    fill={segment.color}
                    stroke="#222"
                    strokeWidth="1"
                  />
                  
                  {/* Text for segment */}
                  <text
                    x={center + (center * 0.65) * Math.cos(startAngle + (segment.degrees / 2) * (Math.PI / 180))}
                    y={center + (center * 0.65) * Math.sin(startAngle + (segment.degrees / 2) * (Math.PI / 180))}
                    fontSize="14"
                    fontWeight="bold"
                    fill="white"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`rotate(${90 + (segment.startAngle + segment.degrees / 2)}, 
                              ${center + (center * 0.65) * Math.cos(startAngle + (segment.degrees / 2) * (Math.PI / 180))}, 
                              ${center + (center * 0.65) * Math.sin(startAngle + (segment.degrees / 2) * (Math.PI / 180))})`}
                  >
                    {segment.name}
                  </text>
                  
                  {/* Value for segment */}
                  {segment.value > 0 && (
                    <text
                      x={center + (center * 0.4) * Math.cos(startAngle + (segment.degrees / 2) * (Math.PI / 180))}
                      y={center + (center * 0.4) * Math.sin(startAngle + (segment.degrees / 2) * (Math.PI / 180))}
                      fontSize="12"
                      fill="white"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      transform={`rotate(${90 + (segment.startAngle + segment.degrees / 2)}, 
                                ${center + (center * 0.4) * Math.cos(startAngle + (segment.degrees / 2) * (Math.PI / 180))}, 
                                ${center + (center * 0.4) * Math.sin(startAngle + (segment.degrees / 2) * (Math.PI / 180))})`}
                    >
                      {segment.value} {mergedConfig.prizes.currency}
                    </text>
                  )}
                </g>
              );
            })}
            
            {/* Center circle */}
            <circle
              cx={center}
              cy={center}
              r={center * 0.1}
              fill="#222"
              stroke="#444"
              strokeWidth="4"
            />
          </svg>
        </div>
        
        {/* Pastel lime green triangle pointer pointing down - positioned higher */}
        <Box
          position="absolute"
          top="-40px"
          left="50%"
          transform="translateX(-50%)"
          zIndex="2"
          width="40px"
          height="40px"
        >
          {/* Triangle pointer pointing down */}
          <Box
            width="0"
            height="0"
            borderLeft="20px solid transparent"
            borderRight="20px solid transparent"
            borderTop="40px solid #B5EAA5" /* Pastel lime green */
            boxShadow="0 0 3px rgba(181, 234, 165, 0.5)" /* Very subtle glow */
            mx="auto"
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box 
      borderRadius="lg" 
      overflow="visible"
      p={8}
      maxW="1400px"
      mx="auto"
      color="white"
      textAlign="center"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      {/* Centered Wheel */}
      <Box 
        width="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        position="relative"
      >
        <FortuneWheelInner />
      </Box>
      
      {/* Title and Description (only if provided) */}
      {(mergedConfig.title || mergedConfig.description) && (
        <Box 
          position="absolute"
          top="10px"
          left="0"
          width="100%"
          textAlign="center"
        >
          {mergedConfig.title && (
            <Text 
              fontSize={mergedConfig.titleFontSize} 
              fontWeight="bold" 
              bgGradient={mergedConfig.titleGradient} 
              bgClip="text"
            >
              {mergedConfig.title}
            </Text>
          )}
          {mergedConfig.description && (
            <Text color={mergedConfig.descriptionColor} mt={1}>
              {mergedConfig.description}
            </Text>
          )}
        </Box>
      )}
      
      {/* Win Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent
          bg="rgba(0, 15, 30, 0.95)"
          color="white"
          borderRadius="lg"
          boxShadow="0 25px 50px rgba(0, 0, 0, 0.7)"
          border="1px solid rgba(0, 128, 255, 0.5)"
        >
          <ModalHeader textAlign="center">
            <Text 
              fontSize="3xl" 
              bgGradient="linear(to-r, blue.400, cyan.400)" 
              bgClip="text"
              fontWeight="extrabold"
            >
              Congratulations!
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} py={4}>
              <Box 
                p={6} 
                bg="rgba(0, 30, 60, 0.8)" 
                borderRadius="full"
                boxShadow="0 0 20px rgba(0, 191, 255, 0.5)"
                border="1px solid rgba(0, 128, 255, 0.5)"
              >
                <FaTrophy size="60px" color="#4299E1" />
              </Box>
              
              <Text fontSize="xl" textAlign="center">
                You've won the {mergedConfig.title} draw!
              </Text>
              
              <Text 
                fontSize="3xl" 
                fontWeight="bold" 
                bgGradient="linear(to-r, blue.400, cyan.400)" 
                bgClip="text"
              >
                {lastWin?.amount} {lastWin?.currency}
              </Text>
              
              <Text color="gray.300" textAlign="center">
                The prize has been added to your balance.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button 
              colorScheme="blue" 
              bgGradient="linear(to-r, blue.500, cyan.500)"
              _hover={{ bgGradient: "linear(to-r, blue.600, cyan.600)" }}
              onClick={onClose} 
              size="lg"
              border="1px solid rgba(0, 128, 255, 0.5)"
            >
              Awesome!
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ReusableFortuneWheel;
